import React, { useState, useEffect } from 'react'
import { Search } from 'lucide-react'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import AccessDenied from '../components/AccessDenied.tsx'
import MessageBanner from '../components/MessageBanner.tsx'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'
import { useColumnResize } from '../hooks/useColumnResize.ts'

interface Agency {
  id: string
  name: string
  address_line1?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  created_at: string
  updated_at: string
  agency_users?: Array<{
    users: {
      id: string
      name: string
      email: string
    }
    role: string
  }>
  company_agency_access?: Array<{
    companies: {
      id: string
      name: string
    }
    can_read: boolean
    can_write: boolean
  }>
}

const AdminAgencySetup: React.FC = () => {
  const { user } = useAuth()
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    fetchAgencies()
  }, [])

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/api/admin/agencies')
      setAgencies(response.data.agencies)
    } catch (error) {
      console.error('Error fetching agencies:', error)
      setError('Failed to load agencies')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setError('')

    try {
      const payload = editingAgency ? { ...formData, id: editingAgency.id } : formData

      await api.post('/api/admin/agencies', payload)

      setMessage(editingAgency ? 'Agency updated successfully!' : 'Agency created successfully!')
      resetForm()
      fetchAgencies()
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save agency')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (agency: Agency) => {
    setEditingAgency(agency)
    setFormData({
      name: agency.name,
      address_line1: agency.address_line1 || '',
      city: agency.city || '',
      state: agency.state || '',
      postal_code: agency.postal_code || '',
      country: agency.country || 'US',
    })
  }

  const handleCancel = () => {
    resetForm()
  }

  const resetForm = () => {
    setEditingAgency(null)
    setFormData({
      name: '',
      address_line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  if (!user?.is_system_admin) {
    return <AccessDenied />
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Agency Creation</h1>

          <MessageBanner message={message} error={error} />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Agency Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter agency name"
                  required
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="AU">Australia</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="address_line1"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Address Line 1
                </label>
                <input
                  type="text"
                  id="address_line1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter address"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                  State/Province
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter state"
                />
              </div>

              <div>
                <label
                  htmlFor="postal_code"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Postal Code
                </label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter postal code"
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || !formData.name}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : editingAgency ? 'Update Agency' : 'Create Agency'}
              </button>

              {editingAgency && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Existing Agencies</h2>
            <AgenciesTable agencies={agencies} onEdit={handleEdit} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAgencySetup

// ===================== Agencies Table =====================

function AgenciesTable({
  agencies,
  onEdit,
}: {
  agencies: Agency[]
  onEdit: (agency: Agency) => void
}) {
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [search, setSearch] = useState('')
  const { widths, startResize } = useColumnResize('admin-agencies-col-widths')

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const filtered = agencies.filter(a => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      a.name.toLowerCase().includes(q) ||
      (a.city || '').toLowerCase().includes(q) ||
      (a.state || '').toLowerCase().includes(q)
    )
  })

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    switch (sortBy) {
      case 'location':
        return (
          ((a.state || '') + (a.city || '')).localeCompare((b.state || '') + (b.city || '')) * dir
        )
      case 'users':
        return ((a.agency_users?.length || 0) - (b.agency_users?.length || 0)) * dir
      case 'companies':
        return (
          ((a.company_agency_access?.length || 0) - (b.company_agency_access?.length || 0)) * dir
        )
      case 'created_at': {
        const at = a.created_at ? new Date(a.created_at).getTime() : 0
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0
        return (at - bt) * dir
      }
      default:
        return a.name.localeCompare(b.name) * dir
    }
  })

  return (
    <div>
      <div className="mb-4 max-w-sm relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search agencies..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <TableContainer
        loading={false}
        hasData={sorted.length > 0}
        skeletonRows={5}
        skeletonCols={5}
        emptyMessage={search ? 'No agencies match your search' : 'No agencies yet'}
      >
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table
            className={`min-w-full divide-y divide-gray-200 ${Object.keys(widths).length > 0 ? 'table-fixed' : ''}`}
          >
            <TableHeader
              columns={[
                { label: 'Agency', key: 'name' },
                { label: 'Location', key: 'location' },
                { label: 'Users', key: 'users' },
                { label: 'Companies', key: 'companies' },
                { label: 'Created', key: 'created_at' },
                { label: '', key: 'actions', sortable: false, resizable: false },
              ]}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={toggleSort}
              widths={widths}
              onResize={startResize}
            />
            <tbody className="bg-white divide-y divide-gray-200">
              {sorted.map(agency => (
                <tr key={agency.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{agency.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {[agency.city, agency.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {agency.agency_users?.length || 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {agency.company_agency_access?.length || 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(agency.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => onEdit(agency)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  )
}
